import { createSlice } from '@reduxjs/toolkit';
import { createAppAsyncThunk } from '../createAppAsyncThunk';
import { API_DOWNLOAD } from '../../services';
import type { RootState } from '../store';
import { env } from '../../env';
import { ENDPOINTS } from '../../constants/endPoints';
import { RADRequest } from '../../models/common/RADRequest';
import { saveBlobAsFile } from '../../utils/appUtils';
import lang from '../../languages/en.json';

interface ScorecardExportState {
  isLoading: boolean;
}

const initialState: ScorecardExportState = {
  isLoading: false,
};

// The API streams the PDF bytes directly through GetScorecard so the browser never
// touches Blob Storage — required because the production storage account is locked
// to the internal network. The server sets Content-Type: application/pdf and
// Content-Disposition: attachment with the download filename; we hand the bytes
// straight to saveBlobAsFile.
export const downloadScorecard = createAppAsyncThunk(
  'scorecardExport/download',
  async (payload: RADRequest) => {
    const fallbackFileName = `Store-${payload.storeNumber}-Scorecard.pdf`;

    const { blob, fileName } = await API_DOWNLOAD(
      `${env.RAD_APP_API_URL}${ENDPOINTS.GET_SCORECARD}`,
      payload,
      fallbackFileName,
    );

    // Reject empty bodies and HTML error pages returned with a 200 status —
    // axios does not throw on those, so we'd otherwise save a corrupt PDF.
    if (!blob || blob.size === 0 || blob.type.includes('text/html')) {
      throw new Error(lang.storeBanner.scorecardExportInvalidResponse);
    }

    saveBlobAsFile(blob, fileName);
  },
);

const scorecardExportSlice = createSlice({
  name: 'scorecardExport',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(downloadScorecard.pending, state => {
        state.isLoading = true;
      })
      .addCase(downloadScorecard.fulfilled, state => {
        state.isLoading = false;
      })
      .addCase(downloadScorecard.rejected, state => {
        state.isLoading = false;
      });
  },
});

export const selectIsScorecardExportLoading = (state: RootState): boolean =>
  state.scorecardExport.isLoading;

export default scorecardExportSlice.reducer;
